import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, ArrowRight, EyeOff, Sparkles } from 'lucide-react';
import VenueBottomNav from '../../components/VenueBottomNav';
import VenueHeader from '../../components/VenueHeader';
import { useVenue } from '../../contexts/VenueContext';
import { getAuthHeaders, getVenueParams } from '../../utils/venueAppApi';
import { VenueAnalyticsChartCard, VenueAnalyticsControls, VenueAnalyticsStatsTiles, VenueAnalyticsTotalProfit } from '../../components/venue/VenueAnalyticsComponents';
import VenueAnalyticsWeeklySheet from '../../components/venue/VenueAnalyticsWeeklySheet';
import { VenueLiveButHiddenBanner } from '../../components/venue/VenueLiveButHiddenBanner';
import { setVenueSetupReturnPath } from '../../utils/venueSetupBackNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VenueAnalyticsPage() {
  const navigate = useNavigate();
  const { activeVenueAppId, activeVenue } = useVenue();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [rangeMode, setRangeMode] = useState('range'); // 'range' (Last 30 days) | 'month'
  const [selectedDay, setSelectedDay] = useState(30);
  const [data, setData] = useState(null);
  const [usingDemo, setUsingDemo] = useState(false);
  const [hideTotal, setHideTotal] = useState(true);
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [weeklySheetMetric, setWeeklySheetMetric] = useState(null);
  const [payoutStatus, setPayoutStatus] = useState(null);
  const sliderRef = useRef(null);
  const isDragging = useRef(false);

  // Surface a clear banner when the venue is "live" but hidden from guests
  // because the owner hasn't connected a payout method yet. Removes guesswork
  // about why a freshly-approved venue isn't showing up on the guest map.
  useEffect(() => {
    if (!activeVenueAppId) return;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/venue/payout-method/${activeVenueAppId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) setPayoutStatus(res.data || null);
      } catch {
        if (!cancelled) setPayoutStatus(null);
      }
    })();
    return () => { cancelled = true; };
  }, [activeVenueAppId]);

  const logVenueAnalyticsDebug = useCallback((message, error) => {
    if (process.env.NODE_ENV === 'development') {
      console.error(message, error);
    }
  }, []);

  const generateEmptyData = useCallback(() => {
    let daily;
    if (rangeMode === 'range') {
      const now = new Date();
      daily = Array.from({ length: 30 }, (_, i) => {
        const d = new Date(now);
        d.setDate(now.getDate() - (29 - i));
        const label = `${d.getDate()} ${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()]}`;
        return { day: d.getDate(), label, month_idx: d.getMonth(), profit: 0, qr_count: 0 };
      });
    } else {
      const daysInMonth = new Date(year, month + 1, 0).getDate();
      daily = Array.from({ length: daysInMonth }, (_, i) => ({
        day: i + 1,
        label: `${i + 1} ${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][month]}`,
        month_idx: month,
        profit: 0,
        qr_count: 0,
      }));
    }
    setData({
      daily_data: daily,
      monthly_profit: 0,
      monthly_qrs: 0,
      total_profit: 0,
      prev_monthly_profit: 0,
      prev_monthly_qrs: 0,
      mom_profit_growth_pct: 0,
      mom_qr_growth_pct: 0,
      avg_ticket: 0,
      peak_hour: null,
      top_day: null,
      mode: rangeMode,
      range_days: rangeMode === 'range' ? 30 : null,
      year,
      month: month + 1,
    });
    setUsingDemo(false);
  }, [month, year, rangeMode]);

  const fetchData = useCallback(async () => {
    try {
      const url = rangeMode === 'range'
        ? `${API}/venue/analytics?range_days=30`
        : `${API}/venue/analytics?year=${year}&month=${month + 1}`;
      const res = await axios.get(url, {
        headers: getAuthHeaders(),
        params: getVenueParams(activeVenueAppId),
      });
      setData(res.data);
      setUsingDemo(false);
    } catch (err) {
      logVenueAnalyticsDebug('Failed to load analytics', err);
      generateEmptyData();
    }
  }, [activeVenueAppId, generateEmptyData, logVenueAnalyticsDebug, month, year, rangeMode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Chart dimensions
  const chartW = 320;
  const chartH = 200;

  const dailyData = data?.daily_data || [];
  const daysInMonth = dailyData.length || 30;

  // Slider handling
  const handleSliderInteraction = useCallback((clientX) => {
    if (!sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, x / rect.width));
    const day = Math.round(ratio * (daysInMonth - 1)) + 1;
    setSelectedDay(day);
  }, [daysInMonth]);

  const handleSliderDown = (e) => {
    isDragging.current = true;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    handleSliderInteraction(clientX);
  };

  const handleSliderMove = useCallback((e) => {
    if (!isDragging.current) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    handleSliderInteraction(clientX);
  }, [handleSliderInteraction]);

  const handleSliderUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  useEffect(() => {
    document.addEventListener('mousemove', handleSliderMove);
    document.addEventListener('mouseup', handleSliderUp);
    document.addEventListener('touchmove', handleSliderMove);
    document.addEventListener('touchend', handleSliderUp);
    return () => {
      document.removeEventListener('mousemove', handleSliderMove);
      document.removeEventListener('mouseup', handleSliderUp);
      document.removeEventListener('touchmove', handleSliderMove);
      document.removeEventListener('touchend', handleSliderUp);
    };
  }, [handleSliderMove, handleSliderUp]);

  const sliderRatio = (selectedDay - 1) / Math.max(daysInMonth - 1, 1);

  return (
    <div
      className="app-shell premium-page"
      data-testid="venue-analytics-page"
      style={{
        minHeight: '100vh',
        backgroundColor: 'var(--app-bg)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingBottom: 120,
      }}
    >
      {/* Header — shared venue selector */}
      <VenueHeader />

      {activeVenue?.status === 'submitted' || activeVenue?.status === 'pending' ? (
        <div
          data-testid="venue-analytics-pending-banner"
          style={{
            width: 'calc(100% - 32px)',
            maxWidth: 360,
            marginTop: 12,
            padding: '12px 14px',
            borderRadius: 14,
            border: '1px solid rgba(214, 137, 16, 0.25)',
            background: 'var(--status-warning-surface)',
            boxShadow: '0 8px 18px rgba(214, 137, 16, 0.12)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            textAlign: 'left',
          }}
        >
          <span style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(214, 137, 16, 0.14)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <AlertTriangle size={16} color="#B56B11" strokeWidth={2.2} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 14, fontWeight: 700, color: 'var(--status-warning-text)' }}>
              Under review by Allure
            </p>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--status-warning-text)', lineHeight: 1.45, fontWeight: 500, opacity: 0.85 }}>
              We’re reviewing your venue. You’ll get a push and email the moment you’re approved. Feel free to keep polishing your photos, vibe and hours in the meantime.
            </p>
          </div>
        </div>
      ) : null}

      {activeVenue?.status === 'on_hold' ? (
        <div
          data-testid="venue-analytics-admin-hold-banner"
          style={{
            width: 'calc(100% - 32px)',
            maxWidth: 360,
            marginTop: 12,
            padding: '12px 14px',
            borderRadius: 14,
            border: '1px solid rgba(214, 137, 16, 0.25)',
            background: 'var(--status-warning-surface)',
            boxShadow: '0 8px 18px rgba(214, 137, 16, 0.12)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            textAlign: 'left',
          }}
        >
          <span style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(214, 137, 16, 0.14)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <AlertTriangle size={16} color="#B56B11" strokeWidth={2.2} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 14, fontWeight: 700, color: 'var(--status-warning-text)' }}>
              Temporarily on hold
            </p>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--status-warning-text)', lineHeight: 1.45, fontWeight: 500, opacity: 0.85 }}>
              {activeVenue?.admin_hold_reason
                ? `Allure paused your venue: ${activeVenue.admin_hold_reason}. You’ll be notified as soon as it’s back live.`
                : 'Allure has paused your venue. You’ll be notified as soon as it’s back live.'}
            </p>
          </div>
        </div>
      ) : null}

      {activeVenue?.status === 'setup_mode' ? (
        <button
          type="button"
          onClick={() => navigate('/venue/setup-mode')}
          data-testid="venue-analytics-setup-mode-banner"
          style={{
            width: 'calc(100% - 32px)',
            maxWidth: 360,
            marginTop: 12,
            padding: '12px 14px',
            borderRadius: 14,
            border: '1px solid var(--card-border)',
            background: 'var(--accent-surface-soft)',
            boxShadow: 'var(--premium-shadow-nested)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            textAlign: 'left',
            cursor: 'pointer',
          }}
        >
          <span style={{ width: 32, height: 32, borderRadius: 10, background: 'var(--accent-surface)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Sparkles size={16} color="var(--accent-purple)" strokeWidth={2.2} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>
              Finish setup to go live
            </p>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-2)', lineHeight: 1.45, fontWeight: 500, opacity: 0.9 }}>
              Add your photos, vibe and operating hours. Once Allure approves your venue, you’ll connect a payout method to go live.
            </p>
            <span style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 9999, background: 'var(--accent-purple)', color: '#FFFFFF', fontFamily: "'Inria Serif', serif", fontSize: 12, fontWeight: 700, boxShadow: 'var(--premium-shadow-main)' }}>
              Complete your checklist
              <ArrowRight size={12} strokeWidth={2.4} />
            </span>
          </div>
        </button>
      ) : null}

      {activeVenue?.status === 'approved' && payoutStatus && payoutStatus.is_on_hold ? (
        <button
          type="button"
          onClick={() => navigate('/venue/settings/payouts')}
          data-testid="venue-analytics-on-hold-banner"
          style={{
            width: 'calc(100% - 32px)',
            maxWidth: 360,
            marginTop: 12,
            padding: '12px 14px',
            borderRadius: 14,
            border: '1px solid rgba(214, 137, 16, 0.25)',
            background: 'var(--status-warning-surface)',
            boxShadow: '0 8px 18px rgba(214, 137, 16, 0.12)',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 10,
            textAlign: 'left',
            cursor: 'pointer',
          }}
        >
          <span style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(214, 137, 16, 0.14)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <AlertTriangle size={16} color="#B56B11" strokeWidth={2.2} />
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontFamily: "'Inria Serif', serif", fontSize: 14, fontWeight: 700, color: 'var(--status-warning-text)' }}>
              Your venue is hidden from guests
            </p>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--status-warning-text)', lineHeight: 1.45, fontWeight: 500, opacity: 0.85 }}>
              Allure approved you — but the map won’t show your venue until a payout method is connected so guests can actually pay you.
            </p>
            <span style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 9999, background: 'linear-gradient(135deg, #F39A24, #D6890F)', color: '#FFFFFF', fontFamily: "'Inria Serif', serif", fontSize: 12, fontWeight: 700, boxShadow: '0 8px 16px rgba(214, 137, 16, 0.30)' }}>
              Connect payout to go live
              <ArrowRight size={12} strokeWidth={2.4} />
            </span>
          </div>
        </button>
      ) : null}

      {/*
        Missing-setup banner — surfaces WHY a live venue is hidden. Fires
        whenever the venue is in `live` state but the venues doc has
        `is_active=false` (auto-hidden by the backend because photos < 10,
        no operating hours, incomplete vibe, or payout disconnected).
        Lists every missing piece so the owner knows exactly what to fix.
        Shared component — same banner now also appears on the Scan page
        so an owner working the keypad all day can't miss it.
      */}
      <VenueLiveButHiddenBanner fromPath="/venue/analytics" testIdPrefix="venue-analytics" />

      <VenueAnalyticsControls
        data={data}
        month={month}
        setMonth={setMonth}
        setShowMonthPicker={setShowMonthPicker}
        setShowYearPicker={setShowYearPicker}
        year={year}
        setYear={setYear}
        showMonthPicker={showMonthPicker}
        showYearPicker={showYearPicker}
        rangeMode={rangeMode}
        setRangeMode={setRangeMode}
      />

      {!usingDemo && data ? (
        <>
          <VenueAnalyticsChartCard
            chartH={chartH}
            chartW={chartW}
            dailyData={dailyData}
            data={data}
            handleSliderDown={handleSliderDown}
            month={month}
            selectedDay={selectedDay}
            sliderRatio={sliderRatio}
            sliderRef={sliderRef}
            onOpenWeekly={(metric) => setWeeklySheetMetric(metric)}
          />

          <VenueAnalyticsTotalProfit data={data} hideTotal={hideTotal} setHideTotal={setHideTotal} />

          <VenueAnalyticsStatsTiles data={data} />
        </>
      ) : null}

      {/* Bottom nav */}
      <VenueBottomNav />

      <VenueAnalyticsWeeklySheet
        open={weeklySheetMetric !== null}
        onClose={() => setWeeklySheetMetric(null)}
        data={data}
        metric={weeklySheetMetric || 'profit'}
      />
    </div>
  );
}
